import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import DeliveryTransportPicker from '../../transports/components/DeliveryTransportPicker';
import { fetchSupplierOrderTransports } from '../api/orderTransportApi';
import type { SupplierOrderTransportDto } from '../../../dto/transport';

/**
 * Etap 2 (transport przywozowy): "which trucks are bringing this order in",
 * on the supplier order itself.
 *
 * Read-only by design — a transport's links are owned by the transport form,
 * and this page is where the operator checks the delivery, not where she plans
 * it. Each row still opens its transport, and shows the PZ documents already
 * issued off that truck, so "what is left to receive" is answerable here.
 *
 * Kept as its own section component rather than more state on SupplierOrderDetail:
 * the page is already ~2000 lines, and this way the fetch/empty/failure states
 * have a seam that can be tested without mounting the whole order page.
 */
interface SupplierOrderDeliveryTransportsSectionProps {
  supplierOrderId?: string;
  onOpenTransport: (transportId: string) => void;
}

const SupplierOrderDeliveryTransportsSection: React.FC<SupplierOrderDeliveryTransportsSectionProps> = ({
  supplierOrderId,
  onOpenTransport,
}) => {
  const { t } = useTranslation(['orders']);
  const [transports, setTransports] = useState<SupplierOrderTransportDto[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadFailed, setLoadFailed] = useState(false);

  const loadTransports = useCallback(async () => {
    if (!supplierOrderId) {
      return;
    }

    setLoading(true);
    setLoadFailed(false);
    try {
      setTransports(await fetchSupplierOrderTransports(supplierOrderId));
    } catch (error) {
      // Advisory only: a failure here degrades this one section, it never
      // blocks the rest of the supplier order.
      setTransports([]);
      setLoadFailed(true);
    } finally {
      setLoading(false);
    }
  }, [supplierOrderId]);

  useEffect(() => {
    void loadTransports();
  }, [loadTransports]);

  return (
    <DeliveryTransportPicker
      readOnly
      transports={transports}
      value={transports.map((transport) => transport.id)}
      loading={loading}
      loadFailed={loadFailed}
      onRetry={() => { void loadTransports(); }}
      onOpenTransport={onOpenTransport}
      emptyMessage={t('orders:supplierOrders.detail.deliveryTransportsEmpty')}
      data-testid="supplier-order-delivery-transport-list"
    />
  );
};

export default SupplierOrderDeliveryTransportsSection;
